import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  MessageCircle,
  Send,
  Flag,
  Flame,
  Clock,
  TrendingUp,
  Loader2,
  MoreHorizontal,
  X,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

// ============================================
// POST CARD COMPONENT
// ============================================
const PostCard = ({ post, onVote, onOpenPost, onFlag }) => {
  const [isVoting, setIsVoting] = useState(false);

  const handleVote = async (e, voteType) => {
    e.stopPropagation();
    if (isVoting) return;

    setIsVoting(true);
    try {
      await onVote(post.id, voteType);
    } finally {
      setIsVoting(false);
    }
  };

  const handleFlag = (e) => {
    e.stopPropagation();
    onFlag(post.id);
  };

  const currentVote = post.user_vote;
  const upvoteActive = currentVote === 1;
  const downvoteActive = currentVote === -1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      layout
      onClick={() => onOpenPost(post)}
      className="group cursor-pointer"
    >
      <div
        className="p-5 rounded-2xl transition-all duration-200 hover:shadow-lg border"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Content */}
        <p
          className="text-slate-800 leading-relaxed text-base whitespace-pre-wrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {post.content}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          {/* Vote Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleVote(e, upvoteActive ? 0 : 1)}
              disabled={isVoting}
              className={`p-2 rounded-xl transition-all ${
                upvoteActive
                  ? 'bg-green-100 text-green-600'
                  : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
              }`}
            >
              <ArrowUp className="w-5 h-5" />
            </button>
            <span
              className={`text-base font-semibold min-w-[32px] text-center ${
                post.score > 0 ? 'text-green-600' : post.score < 0 ? 'text-red-500' : 'text-slate-500'
              }`}
            >
              {post.score}
            </span>
            <button
              onClick={(e) => handleVote(e, downvoteActive ? 0 : -1)}
              disabled={isVoting}
              className={`p-2 rounded-xl transition-all ${
                downvoteActive
                  ? 'bg-red-100 text-red-500'
                  : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
              }`}
            >
              <ArrowDown className="w-5 h-5" />
            </button>
          </div>

          {/* Reply Count & Time */}
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-slate-400 text-sm">
              <MessageCircle className="w-4 h-4" />
              {post.reply_count}
            </span>
            <span className="text-slate-400 text-sm">{post.time_ago}</span>

            {/* More Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                >
                  <MoreHorizontal className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleFlag} className="text-red-500">
                  <Flag className="w-4 h-4 mr-2" />
                  Report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// COMMENT COMPONENT
// ============================================
const Comment = ({ comment, onVote }) => {
  const [isVoting, setIsVoting] = useState(false);

  const handleVote = async (voteType) => {
    if (isVoting) return;
    setIsVoting(true);
    try {
      await onVote(comment.id, voteType);
    } finally {
      setIsVoting(false);
    }
  };

  const currentVote = comment.user_vote;
  const upvoteActive = currentVote === 1;
  const downvoteActive = currentVote === -1;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="py-4 border-b border-slate-100 last:border-b-0"
    >
      <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'Inter', sans-serif" }}>
        {comment.content}
      </p>
      <div className="flex items-center gap-4 mt-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleVote(upvoteActive ? 0 : 1)}
            disabled={isVoting}
            className={`p-1.5 rounded-lg transition-all ${
              upvoteActive ? 'text-green-600 bg-green-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <span className={`text-sm font-medium min-w-[20px] text-center ${
            comment.score > 0 ? 'text-green-600' : comment.score < 0 ? 'text-red-500' : 'text-slate-400'
          }`}>
            {comment.score}
          </span>
          <button
            onClick={() => handleVote(downvoteActive ? 0 : -1)}
            disabled={isVoting}
            className={`p-1.5 rounded-lg transition-all ${
              downvoteActive ? 'text-red-500 bg-red-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>
        <span className="text-sm text-slate-400">{comment.time_ago}</span>
      </div>
    </motion.div>
  );
};

// ============================================
// POST DETAIL MODAL
// ============================================
const PostDetailModal = ({ post, onClose, onVotePost, onAddComment }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localPost, setLocalPost] = useState(post);

  useEffect(() => {
    loadPostDetails();
  }, [post.id]);

  const loadPostDetails = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE}/api/feed/posts/${post.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
        setLocalPost(data.post);
      }
    } catch (error) {
      console.error('Failed to load post details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to comment');
        return;
      }

      const response = await fetch(`${API_BASE}/api/feed/posts/${post.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ content: newComment.trim() })
      });

      if (response.ok) {
        const comment = await response.json();
        setComments(prev => [...prev, comment]);
        setNewComment('');
        onAddComment(post.id);
        toast.success('Reply posted!');
      } else if (response.status === 429) {
        toast.error('Slow down! Try again in a bit.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
      toast.error('Failed to post reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoteComment = async (commentId, voteType) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Optimistic update
      setComments(prev =>
        prev.map(c => {
          if (c.id !== commentId) return c;
          const oldVote = c.user_vote || 0;
          const scoreDiff = voteType - oldVote;
          return {
            ...c,
            score: c.score + scoreDiff,
            user_vote: voteType === 0 ? null : voteType
          };
        })
      );

      const response = await fetch(`${API_BASE}/api/feed/comments/${commentId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ vote_type: voteType })
      });

      if (response.ok) {
        const data = await response.json();
        setComments(prev =>
          prev.map(c =>
            c.id === commentId
              ? { ...c, score: data.new_score, user_vote: data.user_vote }
              : c
          )
        );
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleVotePost = async (voteType) => {
    const oldVote = localPost.user_vote || 0;
    const scoreDiff = voteType - oldVote;

    // Optimistic update
    setLocalPost(prev => ({
      ...prev,
      score: prev.score + scoreDiff,
      user_vote: voteType === 0 ? null : voteType
    }));

    await onVotePost(post.id, voteType);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Post
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Original Post */}
          <div className="mb-6">
            <p className="text-lg text-slate-800 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'Inter', sans-serif" }}>
              {localPost.content}
            </p>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleVotePost(localPost.user_vote === 1 ? 0 : 1)}
                  className={`p-2 rounded-xl transition-all ${
                    localPost.user_vote === 1 ? 'bg-green-100 text-green-600' : 'hover:bg-slate-100 text-slate-400'
                  }`}
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
                <span className={`text-base font-semibold min-w-[32px] text-center ${
                  localPost.score > 0 ? 'text-green-600' : localPost.score < 0 ? 'text-red-500' : 'text-slate-500'
                }`}>
                  {localPost.score}
                </span>
                <button
                  onClick={() => handleVotePost(localPost.user_vote === -1 ? 0 : -1)}
                  className={`p-2 rounded-xl transition-all ${
                    localPost.user_vote === -1 ? 'bg-red-100 text-red-500' : 'hover:bg-slate-100 text-slate-400'
                  }`}
                >
                  <ArrowDown className="w-5 h-5" />
                </button>
              </div>
              <span className="text-sm text-slate-400">{localPost.time_ago}</span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200 my-6" />

          {/* Comments */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wide">
              {comments.length} {comments.length === 1 ? 'Reply' : 'Replies'}
            </h3>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-slate-400 text-center py-12">
                No replies yet. Be the first!
              </p>
            ) : (
              <div>
                {comments.map(comment => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    onVote={handleVoteComment}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Comment Input */}
        <div className="p-5 border-t border-slate-100 bg-slate-50">
          <div className="flex gap-3">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a reply..."
              className="min-h-[50px] max-h-[120px] resize-none text-base bg-white rounded-xl"
              maxLength={300}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitComment();
                }
              }}
            />
            <Button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || isSubmitting}
              size="icon"
              className="shrink-0 h-12 w-12 rounded-xl"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-right">
            {newComment.length}/300
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// MAIN FEED PAGE
// ============================================
const FeedPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortBy, setSortBy] = useState('hot');
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPost, setNewPost] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const textareaRef = useRef(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/connect-email');
    }
  }, [user, authLoading, navigate]);

  // Load posts on mount and when sort changes
  useEffect(() => {
    if (user) {
      loadPosts();
    }
  }, [sortBy, user]);

  // Real-time subscription for new posts
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('anon_feed_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'anon_posts' },
        (payload) => {
          if (sortBy === 'new') {
            setPosts(prev => [{
              ...payload.new,
              time_ago: 'just now',
              user_vote: null
            }, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, sortBy]);

  const loadPosts = async (cursor = null) => {
    if (isLoading) return;

    if (cursor) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const params = new URLSearchParams({
        sort: sortBy,
        limit: '20'
      });
      if (cursor) params.append('cursor', cursor);

      const response = await fetch(`${API_BASE}/api/feed/posts?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (cursor) {
          setPosts(prev => [...prev, ...data.posts]);
        } else {
          setPosts(data.posts);
        }
        setHasMore(data.has_more);
        setNextCursor(data.next_cursor);
      } else {
        const error = await response.json();
        console.error('Failed to load posts:', error);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
      toast.error('Failed to load feed');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSubmitPost = async () => {
    if (!newPost.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to post');
        return;
      }

      const response = await fetch(`${API_BASE}/api/feed/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ content: newPost.trim() })
      });

      if (response.ok) {
        const post = await response.json();
        setPosts(prev => [post, ...prev]);
        setNewPost('');
        toast.success('Posted anonymously!');

        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      } else if (response.status === 429) {
        toast.error('Slow down! You can post 5 times per hour.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post');
      }
    } catch (error) {
      console.error('Failed to post:', error);
      toast.error('Failed to post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVotePost = async (postId, voteType) => {
    // Optimistic update
    setPosts(prev =>
      prev.map(p => {
        if (p.id !== postId) return p;
        const oldVote = p.user_vote || 0;
        const scoreDiff = voteType - oldVote;
        return {
          ...p,
          score: p.score + scoreDiff,
          user_vote: voteType === 0 ? null : voteType
        };
      })
    );

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE}/api/feed/posts/${postId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ vote_type: voteType })
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev =>
          prev.map(p =>
            p.id === postId
              ? { ...p, score: data.new_score, user_vote: data.user_vote }
              : p
          )
        );
      }
    } catch (error) {
      console.error('Failed to vote:', error);
      // Reload posts on error to sync state
      loadPosts();
    }
  };

  const handleFlagPost = async (postId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE}/api/feed/posts/${postId}/flag`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        toast.success('Post reported. Thanks for keeping the community safe!');
      } else if (response.status === 429) {
        toast.error('Too many reports. Try again later.');
      }
    } catch (error) {
      console.error('Failed to flag:', error);
    }
  };

  const handleAddComment = (postId) => {
    setPosts(prev =>
      prev.map(p =>
        p.id === postId ? { ...p, reply_count: p.reply_count + 1 } : p
      )
    );
  };

  const sortOptions = [
    { value: 'hot', label: 'Hot', icon: Flame },
    { value: 'new', label: 'New', icon: Clock },
    { value: 'top', label: 'Top', icon: TrendingUp }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf9f7]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed background layer - GPU accelerated */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/bluewhite.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
          transform: 'translateZ(0)',
        }}
      />
      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          background: 'rgba(255, 252, 248, 0.98)',
          borderColor: 'rgba(0, 0, 0, 0.06)'
        }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/roadmap')}
                className="rounded-xl"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1
                className="text-xl font-bold text-slate-800"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Anonymous Feed
              </h1>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => loadPosts()}
              disabled={isRefreshing}
              className="rounded-xl"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Create Post Card */}
        <div
          className="rounded-2xl p-5 mb-6 border"
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderColor: 'rgba(0, 0, 0, 0.06)'
          }}
        >
          <Textarea
            ref={textareaRef}
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            placeholder="Share something anonymously... Career hot takes, workplace gossip, anything goes!"
            className="min-h-[100px] resize-none text-base border-0 bg-transparent focus-visible:ring-0 p-0 placeholder:text-slate-400"
            maxLength={500}
          />
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <span className="text-sm text-slate-400">
              {newPost.length}/500
            </span>
            <Button
              onClick={handleSubmitPost}
              disabled={!newPost.trim() || isSubmitting}
              className="gap-2 rounded-xl px-6"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Post
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Sort Tabs */}
        <div className="flex gap-2 p-1.5 bg-white/80 rounded-xl mb-6 border border-slate-100">
          {sortOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                sortBy === option.value
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </button>
          ))}
        </div>

        {/* Posts Feed */}
        <div className="space-y-4">
          {isRefreshing && posts.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No posts yet</h3>
              <p className="text-slate-400">
                Be the first to share something!
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  onVote={handleVotePost}
                  onOpenPost={setSelectedPost}
                  onFlag={handleFlagPost}
                />
              ))}
            </AnimatePresence>
          )}

          {hasMore && (
            <div className="flex justify-center pt-4 pb-8">
              <Button
                variant="outline"
                onClick={() => loadPosts(nextCursor)}
                disabled={isLoading}
                className="rounded-xl"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Load More
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Post Detail Modal */}
      <AnimatePresence>
        {selectedPost && (
          <PostDetailModal
            post={selectedPost}
            onClose={() => setSelectedPost(null)}
            onVotePost={handleVotePost}
            onAddComment={handleAddComment}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FeedPage;
